import React, { useState, useEffect } from 'react';
import { 
    Store, Package, BarChart3, Plus, Edit2, Trash2, 
    ExternalLink, DollarSign, Eye, MousePointer, Loader2
} from 'lucide-react';
import { apiFetch } from '../utils/api';

/**
 * AdsAdmin - Admin panel for managing sponsors, products and ad analytics
 */
const AdsAdmin = () => {
    const [activeTab, setActiveTab] = useState('sponsors');
    const [sponsors, setSponsors] = useState([]);
    const [products, setProducts] = useState([]);
    const [analytics, setAnalytics] = useState([]);
    const [loading, setLoading] = useState(true);
    const [featureEnabled, setFeatureEnabled] = useState(null);
    const [showSponsorForm, setShowSponsorForm] = useState(false);
    const [showProductForm, setShowProductForm] = useState(false);
    const [editingSponsor, setEditingSponsor] = useState(null);
    const [editingProduct, setEditingProduct] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await apiFetch('/api/club/beta-features');
                const data = res.ok ? await res.json() : null;
                const enabled = !!data?.enabled?.ads_admin;
                if (cancelled) return;
                setFeatureEnabled(enabled);
                if (enabled) {
                    await fetchData();
                } else {
                    setLoading(false);
                }
            } catch (err) {
                console.error(err);
                if (!cancelled) {
                    setFeatureEnabled(false);
                    setLoading(false);
                }
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [sponsorsRes, productsRes, analyticsRes] = await Promise.all([
                apiFetch('/api/sponsors'),
                apiFetch('/api/products'),
                apiFetch('/api/ads/analytics?days=30')
            ]);

            if (sponsorsRes.ok) setSponsors(await sponsorsRes.json());
            if (productsRes.ok) setProducts(await productsRes.json());
            if (analyticsRes.ok) setAnalytics(await analyticsRes.json());
        } catch (err) {
            console.error('Fetch error:', err);
        } finally {
            setLoading(false);
        }
    };

    const tabs = [
        { id: 'sponsors', label: 'Sponsorit', icon: <Store size={16} /> },
        { id: 'products', label: 'Tuotteet', icon: <Package size={16} /> },
        { id: 'analytics', label: 'Analytiikka', icon: <BarChart3 size={16} /> }
    ];

    if (loading || featureEnabled === null) {
        return (
            <div className="ads-admin-loading">
                <Loader2 size={32} className="spin" />
                <span>Ladataan mainoshallintaa...</span>
            </div>
        );
    }

    if (!featureEnabled) {
        return (
            <div className="ads-admin" style={{ padding: '1.5rem' }}>
                <h2>Mainosten hallinta</h2>
                <p style={{ color: '#7f8c8d' }}>
                    Tämä toiminto ei ole aktivoitu tälle klubille. SuperAdmin voi ottaa sen käyttöön
                    klubin beta-toiminnoista.
                </p>
            </div>
        );
    }

    return (
        <div className="ads-admin">
            <h2>Mainosten hallinta</h2>

            {/* Tabs */}
            <div className="admin-tabs">
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        className={`admin-tab ${activeTab === tab.id ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab.id)}
                    >
                        {tab.icon}
                        <span>{tab.label}</span>
                    </button>
                ))}
            </div>

            {/* Sponsors Tab */}
            {activeTab === 'sponsors' && (
                <div className="admin-section">
                    <div className="section-header">
                        <h3>Sponsorit ({sponsors.length})</h3>
                        <button className="btn-primary" onClick={() => setShowSponsorForm(true)}>
                            <Plus size={16} /> Lisää sponsori
                        </button>
                    </div>

                    <div className="sponsors-grid">
                        {sponsors.map(sponsor => (
                            <div key={sponsor.id} className={`sponsor-card tier-${sponsor.tier}`}>
                                <div className="sponsor-header">
                                    {sponsor.logo_url && (
                                        <img src={sponsor.logo_url} alt="" className="sponsor-logo" />
                                    )}
                                    <div className="sponsor-info">
                                        <h4>{sponsor.name}</h4>
                                        <span className="sponsor-tier">{sponsor.tier}</span>
                                    </div>
                                </div>
                                <div className="sponsor-details">
                                    <p>
                                        <DollarSign size={14} />
                                        Komissio: {sponsor.commission_percent}%
                                    </p>
                                    <p>
                                        <Package size={14} />
                                        Tuotteita: {products.filter(p => p.sponsor_id === sponsor.id).length}
                                    </p>
                                    {sponsor.affiliate_code && (
                                        <p className="affiliate-code">
                                            Koodi: <code>{sponsor.affiliate_code}</code>
                                        </p>
                                    )}
                                </div>
                                <div className="sponsor-actions">
                                    <a href={sponsor.website_url} target="_blank" rel="noopener noreferrer">
                                        <ExternalLink size={14} />
                                    </a>
                                    <button onClick={() => { setEditingSponsor(sponsor); setShowSponsorForm(true); }}>
                                        <Edit2 size={14} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Products Tab */}
            {activeTab === 'products' && (
                <div className="admin-section">
                    <div className="section-header">
                        <h3>Tuotteet ({products.length})</h3>
                        <button className="btn-primary" onClick={() => setShowProductForm(true)}>
                            <Plus size={16} /> Lisää tuote
                        </button>
                    </div>

                    <table className="products-table">
                        <thead>
                            <tr>
                                <th>Tuote</th>
                                <th>Sponsori</th>
                                <th>Hinta</th>
                                <th>Kategoria</th>
                                <th>Prioriteetti</th>
                                <th>Toiminnot</th>
                            </tr>
                        </thead>
                        <tbody>
                            {products.map(product => (
                                <tr key={product.id}>
                                    <td className="product-cell">
                                        {product.image_url && (
                                            <img src={product.image_url} alt="" className="product-thumb" />
                                        )}
                                        <span>{product.name}</span>
                                    </td>
                                    <td>{product.sponsor_name}</td>
                                    <td>
                                        {product.sale_price ? (
                                            <>
                                                <s>{product.price}€</s> <strong className="sale">{product.sale_price}€</strong>
                                            </>
                                        ) : (
                                            `${product.price}€`
                                        )}
                                    </td>
                                    <td>{product.category || '-'}</td>
                                    <td>{product.priority}</td>
                                    <td>
                                        <button onClick={() => { setEditingProduct(product); setShowProductForm(true); }}>
                                            <Edit2 size={14} />
                                        </button>
                                        <button className="danger" onClick={async () => {
                                            if (window.confirm('Poista tuote?')) {
                                                await apiFetch(`/api/products/${product.id}`, { method: 'DELETE' });
                                                fetchData();
                                            }
                                        }}>
                                            <Trash2 size={14} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Analytics Tab */}
            {activeTab === 'analytics' && (
                <div className="admin-section">
                    <div className="section-header">
                        <h3>Mainosanalytiikka (30 päivää)</h3>
                    </div>

                    <div className="analytics-grid">
                        {analytics.map(stat => (
                            <div key={stat.sponsor_id} className="analytics-card">
                                <h4>{stat.sponsor_name}</h4>
                                <div className="analytics-stats">
                                    <div className="stat">
                                        <Eye size={18} />
                                        <span className="stat-value">{stat.impressions || 0}</span>
                                        <span className="stat-label">Näyttöjä</span>
                                    </div>
                                    <div className="stat">
                                        <MousePointer size={18} />
                                        <span className="stat-value">{stat.clicks || 0}</span>
                                        <span className="stat-label">Klikkejä</span>
                                    </div>
                                    <div className="stat">
                                        <BarChart3 size={18} />
                                        <span className="stat-value">{(stat.ctr || 0).toFixed(2)}%</span>
                                        <span className="stat-label">CTR</span>
                                    </div>
                                    <div className="stat">
                                        <DollarSign size={18} />
                                        <span className="stat-value">{(stat.revenue || 0).toFixed(2)}€</span>
                                        <span className="stat-label">Tuotto</span>
                                    </div>
                                </div>
                            </div>
                        ))}

                        {analytics.length === 0 && (
                            <p style={{ color: '#888', textAlign: 'center', gridColumn: '1 / -1', padding: '2rem' }}>
                                Ei analytiikkadataa valitulta ajanjaksolta.
                            </p>
                        )}
                    </div>
                </div>
            )}

            {/* Sponsor Form Modal */}
            {showSponsorForm && (
                <SponsorForm
                    sponsor={editingSponsor}
                    onSave={async (data) => {
                        const method = editingSponsor ? 'PUT' : 'POST';
                        const url = editingSponsor ? `/api/sponsors/${editingSponsor.id}` : '/api/sponsors';
                        await apiFetch(url, {
                            method,
                            body: JSON.stringify(data)
                        });
                        setShowSponsorForm(false);
                        setEditingSponsor(null);
                        fetchData();
                    }}
                    onClose={() => { setShowSponsorForm(false); setEditingSponsor(null); }}
                />
            )}

            {/* Product Form Modal */}
            {showProductForm && (
                <ProductForm
                    product={editingProduct}
                    sponsors={sponsors}
                    onSave={async (data) => {
                        const method = editingProduct ? 'PUT' : 'POST';
                        const url = editingProduct ? `/api/products/${editingProduct.id}` : '/api/products';
                        await apiFetch(url, {
                            method,
                            body: JSON.stringify(data)
                        });
                        setShowProductForm(false);
                        setEditingProduct(null);
                        fetchData();
                    }}
                    onClose={() => { setShowProductForm(false); setEditingProduct(null); }}
                />
            )}

            <style>{`
                .ads-admin {
                    padding: 1.5rem;
                    max-width: 1200px;
                    margin: 0 auto;
                }

                .ads-admin h2 {
                    color: #f0f6fc;
                    margin-bottom: 1.5rem;
                }

                .ads-admin-loading {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    gap: 1rem;
                    padding: 3rem;
                    color: #8b949e;
                }

                .spin { animation: spin 1s linear infinite; }
                @keyframes spin { to { transform: rotate(360deg); } }

                .admin-tabs {
                    display: flex;
                    gap: 0.5rem;
                    margin-bottom: 1.5rem;
                    border-bottom: 2px solid #30363d;
                    padding-bottom: 0.5rem;
                }

                .admin-tab {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.75rem 1rem;
                    background: transparent;
                    border: none;
                    color: #8b949e;
                    cursor: pointer;
                    font-size: 0.9rem;
                    border-radius: 6px 6px 0 0;
                    transition: all 0.2s;
                }

                .admin-tab:hover {
                    color: #c9d1d9;
                    background: rgba(255, 255, 255, 0.05);
                }

                .admin-tab.active {
                    color: #58a6ff;
                    border-bottom: 2px solid #58a6ff;
                    margin-bottom: -2px;
                }

                .admin-section {
                    background: #161b22;
                    border: 1px solid #30363d;
                    border-radius: 8px;
                    padding: 1.5rem;
                }

                .section-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 1rem;
                }

                .section-header h3 {
                    margin: 0;
                    color: #f0f6fc;
                }

                .btn-primary {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.6rem 1rem;
                    background: #238636;
                    border: none;
                    border-radius: 6px;
                    color: #fff;
                    font-size: 0.85rem;
                    cursor: pointer;
                }

                .btn-primary:hover {
                    background: #2ea043;
                }

                /* Sponsors Grid */
                .sponsors-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
                    gap: 1rem;
                }

                .sponsor-card {
                    background: rgba(0, 0, 0, 0.3);
                    border: 1px solid #374151;
                    border-radius: 8px;
                    padding: 1rem;
                }

                .sponsor-card.tier-platinum {
                    border-color: #a855f7;
                    background: rgba(168, 85, 247, 0.1);
                }

                .sponsor-card.tier-gold {
                    border-color: #f59e0b;
                    background: rgba(245, 158, 11, 0.1);
                }

                .sponsor-header {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    margin-bottom: 0.75rem;
                }

                .sponsor-logo {
                    width: 48px;
                    height: 48px;
                    border-radius: 8px;
                    object-fit: contain;
                    background: #fff;
                    padding: 4px;
                }

                .sponsor-info h4 {
                    margin: 0;
                    color: #f0f6fc;
                }

                .sponsor-tier {
                    font-size: 0.7rem;
                    text-transform: uppercase;
                    color: #8b949e;
                }

                .sponsor-details {
                    font-size: 0.85rem;
                    color: #9ca3af;
                }

                .sponsor-details p {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    margin: 0.25rem 0;
                }

                .affiliate-code code {
                    background: rgba(0, 0, 0, 0.3);
                    padding: 0.2rem 0.4rem;
                    border-radius: 4px;
                    font-family: monospace;
                }

                .sponsor-actions {
                    display: flex;
                    gap: 0.5rem;
                    margin-top: 0.75rem;
                    padding-top: 0.75rem;
                    border-top: 1px solid #374151;
                }

                .sponsor-actions button,
                .sponsor-actions a {
                    background: rgba(255, 255, 255, 0.1);
                    border: none;
                    color: #9ca3af;
                    padding: 0.4rem;
                    border-radius: 4px;
                    cursor: pointer;
                    display: flex;
                }

                .sponsor-actions button:hover,
                .sponsor-actions a:hover {
                    background: rgba(255, 255, 255, 0.2);
                    color: #fff;
                }

                /* Products Table */
                .products-table {
                    width: 100%;
                    border-collapse: collapse;
                }

                .products-table th,
                .products-table td {
                    padding: 0.75rem;
                    text-align: left;
                    border-bottom: 1px solid #30363d;
                }

                .products-table th {
                    color: #8b949e;
                    font-weight: 500;
                    font-size: 0.8rem;
                    text-transform: uppercase;
                }

                .products-table td {
                    color: #c9d1d9;
                }

                .product-cell {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                }

                .product-thumb {
                    width: 40px;
                    height: 40px;
                    border-radius: 4px;
                    object-fit: cover;
                }

                .products-table .sale {
                    color: #22c55e;
                }

                .products-table button {
                    background: transparent;
                    border: none;
                    color: #8b949e;
                    padding: 0.3rem;
                    cursor: pointer;
                    border-radius: 4px;
                }

                .products-table button:hover {
                    background: rgba(255, 255, 255, 0.1);
                    color: #58a6ff;
                }

                .products-table button.danger:hover {
                    color: #ef4444;
                }

                /* Analytics */
                .analytics-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
                    gap: 1rem;
                }

                .analytics-card {
                    background: rgba(0, 0, 0, 0.3);
                    border: 1px solid #374151;
                    border-radius: 8px;
                    padding: 1rem;
                }

                .analytics-card h4 {
                    margin: 0 0 1rem 0;
                    color: #f0f6fc;
                }

                .analytics-stats {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 0.75rem;
                }

                .stat {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    padding: 0.5rem;
                    background: rgba(255, 255, 255, 0.05);
                    border-radius: 6px;
                }

                .stat svg {
                    color: #58a6ff;
                    margin-bottom: 0.25rem;
                }

                .stat-value {
                    font-size: 1.2rem;
                    font-weight: bold;
                    color: #f0f6fc;
                }

                .stat-label {
                    font-size: 0.7rem;
                    color: #8b949e;
                }

                /* Forms */
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.8);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 10000;
                    padding: 1rem;
                }

                .modal-content {
                    background: #161b22;
                    border: 1px solid #30363d;
                    border-radius: 12px;
                    max-width: 500px;
                    width: 100%;
                    max-height: 90vh;
                    overflow-y: auto;
                }

                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 1rem 1.5rem;
                    border-bottom: 1px solid #30363d;
                }

                .modal-header h3 {
                    margin: 0;
                    color: #f0f6fc;
                }

                .modal-body {
                    padding: 1.5rem;
                }

                .form-group {
                    margin-bottom: 1rem;
                }

                .form-group label {
                    display: block;
                    margin-bottom: 0.5rem;
                    color: #c9d1d9;
                    font-size: 0.9rem;
                }

                .form-group input,
                .form-group select,
                .form-group textarea {
                    width: 100%;
                    padding: 0.6rem;
                    background: #0d1117;
                    border: 1px solid #30363d;
                    border-radius: 6px;
                    color: #c9d1d9;
                    font-size: 0.9rem;
                }

                .form-row {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 1rem;
                }

                .modal-footer {
                    display: flex;
                    justify-content: flex-end;
                    gap: 0.75rem;
                    padding: 1rem 1.5rem;
                    border-top: 1px solid #30363d;
                }

                .btn-secondary {
                    padding: 0.6rem 1rem;
                    background: #21262d;
                    border: 1px solid #30363d;
                    border-radius: 6px;
                    color: #c9d1d9;
                    cursor: pointer;
                }

                @media (max-width: 768px) {
                    .admin-tabs {
                        overflow-x: auto;
                    }
                    
                    .sponsors-grid {
                        grid-template-columns: 1fr;
                    }
                    
                    .products-table {
                        display: block;
                        overflow-x: auto;
                    }
                }
            `}</style>
        </div>
    );
};

// Sponsor Form Component
const SponsorForm = ({ sponsor, onSave, onClose }) => {
    const [formData, setFormData] = useState({
        name: sponsor?.name || '',
        logoUrl: sponsor?.logo_url || '',
        websiteUrl: sponsor?.website_url || '',
        affiliateCode: sponsor?.affiliate_code || '',
        commissionPercent: sponsor?.commission_percent || 0,
        tier: sponsor?.tier || 'standard'
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>{sponsor ? 'Muokkaa sponsoria' : 'Lisää sponsori'}</h3>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="modal-body">
                        <div className="form-group">
                            <label>Nimi *</label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={e => setFormData({ ...formData, name: e.target.value })}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>Logo URL</label>
                            <input
                                type="url"
                                value={formData.logoUrl}
                                onChange={e => setFormData({ ...formData, logoUrl: e.target.value })}
                            />
                        </div>
                        <div className="form-group">
                            <label>Verkkosivu</label>
                            <input
                                type="url"
                                value={formData.websiteUrl}
                                onChange={e => setFormData({ ...formData, websiteUrl: e.target.value })}
                            />
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Affiliate-koodi</label>
                                <input
                                    type="text"
                                    value={formData.affiliateCode}
                                    onChange={e => setFormData({ ...formData, affiliateCode: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Komissio %</label>
                                <input
                                    type="number"
                                    step="0.1"
                                    value={formData.commissionPercent}
                                    onChange={e => setFormData({ ...formData, commissionPercent: parseFloat(e.target.value) })}
                                />
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Taso</label>
                            <select
                                value={formData.tier}
                                onChange={e => setFormData({ ...formData, tier: e.target.value })}
                            >
                                <option value="standard">Standard</option>
                                <option value="gold">Gold</option>
                                <option value="platinum">Platinum</option>
                            </select>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn-secondary" onClick={onClose}>Peruuta</button>
                        <button type="submit" className="btn-primary">Tallenna</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// Product Form Component
const ProductForm = ({ product, sponsors, onSave, onClose }) => {
    const [formData, setFormData] = useState({
        sponsorId: product?.sponsor_id || (sponsors[0]?.id || ''),
        name: product?.name || '',
        description: product?.description || '',
        imageUrl: product?.image_url || '',
        productUrl: product?.product_url || '',
        price: product?.price || '',
        salePrice: product?.sale_price || '',
        category: product?.category || '',
        tags: product?.tags || '',
        priority: product?.priority || 0
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>{product ? 'Muokkaa tuotetta' : 'Lisää tuote'}</h3>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="modal-body">
                        <div className="form-group">
                            <label>Sponsori *</label>
                            <select
                                value={formData.sponsorId}
                                onChange={e => setFormData({ ...formData, sponsorId: parseInt(e.target.value) })}
                                required
                            >
                                {sponsors.map(s => (
                                    <option key={s.id} value={s.id}>{s.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Tuotenimi *</label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={e => setFormData({ ...formData, name: e.target.value })}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>Kuvaus</label>
                            <textarea
                                value={formData.description}
                                onChange={e => setFormData({ ...formData, description: e.target.value })}
                                rows={3}
                            />
                        </div>
                        <div className="form-group">
                            <label>Kuva URL</label>
                            <input
                                type="url"
                                value={formData.imageUrl}
                                onChange={e => setFormData({ ...formData, imageUrl: e.target.value })}
                            />
                        </div>
                        <div className="form-group">
                            <label>Tuote URL *</label>
                            <input
                                type="url"
                                value={formData.productUrl}
                                onChange={e => setFormData({ ...formData, productUrl: e.target.value })}
                                required
                            />
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Hinta €</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={formData.price}
                                    onChange={e => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Tarjoushinta €</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={formData.salePrice}
                                    onChange={e => setFormData({ ...formData, salePrice: parseFloat(e.target.value) || null })}
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Kategoria</label>
                                <select
                                    value={formData.category}
                                    onChange={e => setFormData({ ...formData, category: e.target.value })}
                                >
                                    <option value="">-- Valitse --</option>
                                    <option value="expansion">Lisäosa</option>
                                    <option value="accessory">Lisävaruste</option>
                                    <option value="game">Peli</option>
                                    <option value="snacks">Naposteltavat</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Prioriteetti</label>
                                <input
                                    type="number"
                                    value={formData.priority}
                                    onChange={e => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn-secondary" onClick={onClose}>Peruuta</button>
                        <button type="submit" className="btn-primary">Tallenna</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AdsAdmin;


